import { useCallback, useEffect, useRef, useState } from 'react';
import { isCashbackEnabled } from '@/presentation/services/featureFlags';
import { useEnableCashbackModal } from '@/presentation/components/EnableCashbackModal';
import { getAllCashbackItems } from '@/infra/blog/useCases/getAllCashbackItems';
import { AllCashbackItems, ModalContent } from '@/infra/blog/strapi/types';
import { useCashbackSuccessModal } from '@/presentation/components/CashbackSuccessModal';
import { useModal } from '@pelando/components';
import { MetadataCashback } from '@/presentation/services/urls';
import { addAfterCashbackActivationModalStorage } from '@/infra/storage/afterCashbackActivationModalStorage';
import { useCurrentUserCache } from '../useCurrentUserCache';
import { useCashbackAuthGuardModal } from '../useCashbackAuthGuardModal';

type HandleDealCashbackClickoutParams = {
  cashbackPercentage: number;
  clickoutUrl: string | null;
  storeName?: string;
};

const DELAY_TO_SHOW_SUCCESS_MODAL = 1000;

// TODO: remover essa função que altera o metadata e adicionar os hooks para gerar o clickoutUrl nesse hook
export function addCashbackValue(
  url: string,
  cashback: MetadataCashback,
  userId?: string
) {
  const urlObj = new URL(url);
  const dataParam = urlObj.searchParams.get('metadata');

  if (!dataParam) return url;

  const dataObj = JSON.parse(dataParam);

  if (userId) delete dataObj.guest_id;

  Object.assign(dataObj, { cashback, user_id: userId });

  const newDataParam = JSON.stringify(dataObj);

  urlObj.searchParams.set('metadata', newDataParam);
  return urlObj.toString();
}

export const useDealCashbackClickoutHandler = () => {
  const [modalContent, setModalContent] = useState<ModalContent>({
    tip: '',
    title: '',
  });
  const isCashbackFeatureEnabled = isCashbackEnabled();
  const { isLogged, loggedUser } = useCurrentUserCache();
  const { showAuthModal } = useCashbackAuthGuardModal();
  const { showEnableCashbackModal } = useEnableCashbackModal({
    modalContent,
  });
  const { showSuccessModal } = useCashbackSuccessModal();
  const { closeModal } = useModal();

  const handleModalContent = useCallback(async () => {
    const { modalContent } = (await getAllCashbackItems()) as AllCashbackItems;
    setModalContent(modalContent);
  }, []);

  const onGoToStoreWithoutCashback = useCallback(
    (clickoutUrl: string | null) => {
      if (!clickoutUrl) return;
      const storeUrl = addCashbackValue(
        clickoutUrl,
        MetadataCashback.REFUSED,
        loggedUser?.id || ''
      );

      window.open(storeUrl, '_blank');
    },
    [loggedUser?.id]
  );

  const handleShowSuccessModal = useCallback(
    (cashbackPercentage: number) => {
      if (isLogged) return;

      setTimeout(() => {
        showSuccessModal(cashbackPercentage);
      }, DELAY_TO_SHOW_SUCCESS_MODAL);
    },
    [isLogged, showSuccessModal]
  );

  const enableCashback = useCallback(
    (cashbackPercentage: number, clickoutUrl: string, storeName: string) => {
      const storeUrl = addCashbackValue(
        clickoutUrl,
        MetadataCashback.ACCEPTED,
        loggedUser?.id
      );

      const opened = window.open(storeUrl, '_blank');

      if (!opened) return;

      closeModal();
      addAfterCashbackActivationModalStorage(storeName || '');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleShowSuccessModal, loggedUser?.id]
  );

  const enableCashbackRef = useRef(enableCashback);
  const goToStoreWithoutCashbackRef = useRef(onGoToStoreWithoutCashback);

  const handleEnableCashbackRetry = useCallback(
    ({
      cashbackPercentage,
      clickoutUrl,
      storeName,
    }: Omit<HandleDealCashbackClickoutParams, 'clickout'> & {
      clickoutUrl: string;
    }) => {
      showEnableCashbackModal({
        onEnableCashback: () =>
          enableCashbackRef.current(
            cashbackPercentage,
            clickoutUrl,
            storeName || ''
          ),
        onGoToStoreWithoutCashback: () =>
          goToStoreWithoutCashbackRef.current(clickoutUrl),
      });
    },
    [showEnableCashbackModal]
  );

  const onEnableCashback = useCallback(
    (cashbackPercentage: number, clickoutUrl: string, storeName: string) => {
      if (isLogged) {
        enableCashbackRef.current(cashbackPercentage, clickoutUrl, storeName);
        return;
      }

      showAuthModal({
        cashbackPercentage,
        retry: () =>
          setTimeout(
            () =>
              handleEnableCashbackRetry({
                cashbackPercentage,
                clickoutUrl,
                storeName,
              }),
            100
          ),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enableCashbackRef, showAuthModal, handleEnableCashbackRetry, isLogged]
  );

  const handleDealCashbackClickout = useCallback(
    ({
      cashbackPercentage,
      clickoutUrl,
      storeName,
    }: HandleDealCashbackClickoutParams) => {
      const hasCashback = isCashbackFeatureEnabled && cashbackPercentage > 0;

      if (!clickoutUrl) return;

      if (hasCashback) {
        showEnableCashbackModal({
          onEnableCashback: () =>
            onEnableCashback(cashbackPercentage, clickoutUrl, storeName || ''),
          onGoToStoreWithoutCashback: () =>
            onGoToStoreWithoutCashback(clickoutUrl),
        });

        return;
      }

      window.open(clickoutUrl, '_blank', 'noopener');
    },
    /* eslint-disable react-hooks/exhaustive-deps */
    [
      isCashbackFeatureEnabled,
      onEnableCashback,
      onGoToStoreWithoutCashback,
      showEnableCashbackModal,
      isLogged,
    ]
  );

  useEffect(() => {
    handleModalContent();
  }, [handleModalContent]);

  useEffect(() => {
    enableCashbackRef.current = enableCashback;
  }, [enableCashback, isLogged]);

  useEffect(() => {
    goToStoreWithoutCashbackRef.current = onGoToStoreWithoutCashback;
  }, [onGoToStoreWithoutCashback, isLogged]);

  return {
    handleDealCashbackClickout,
  };
};
